import {
  ColumnFiltersState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getTableStateFromStorage,
  saveTableStateToStorage,
  TableState,
} from "./tableCachedState";

export interface UseTableStateReturn {
  sorting: SortingState;
  setSorting: (updater: Updater<SortingState>) => void;
  columnFilters: ColumnFiltersState;
  setColumnFilters: (updater: Updater<ColumnFiltersState>) => void;
  globalFilter: string;
  setGlobalFilter: (filter: string) => void;
  globalFilterValue: string;
  setGlobalFilterValue: (value: string) => void;
}

export const useTableState = (
  defaultSort?: SortingState,
  tableKey?: string,
  sortableColumnIds?: ReadonlySet<string>,
): UseTableStateReturn => {
  const location = useLocation();
  const key = tableKey ? `${location.pathname}_${tableKey}` : location.pathname;

  const [tableState, setTableState] = useState<TableState>(() => {
    const stored = getTableStateFromStorage(key);

    const sorting = sortableColumnIds
      ? stored.sorting.filter((sort) => sortableColumnIds.has(sort.id))
      : stored.sorting;

    if (defaultSort && defaultSort.length > 0 && sorting.length === 0) {
      return { ...stored, sorting: defaultSort };
    }

    return { ...stored, sorting };
  });

  useEffect(() => {
    saveTableStateToStorage(key, tableState);
  }, [key, tableState]);

  const setSorting = useCallback((updater: Updater<SortingState>) => {
    setTableState((prev) => {
      const newSorting =
        typeof updater === "function" ? updater(prev.sorting) : updater;

      return { ...prev, sorting: newSorting };
    });
  }, []);

  const setColumnFilters = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      setTableState((prev) => {
        const newFilters =
          typeof updater === "function" ? updater(prev.columnFilters) : updater;

        return { ...prev, columnFilters: newFilters };
      });
    },
    [],
  );

  const setGlobalFilter = useCallback((filter: string) => {
    setTableState((prev) => ({ ...prev, globalFilter: filter }));
  }, []);

  const setGlobalFilterValue = useCallback((value: string) => {
    setTableState((prev) => ({ ...prev, globalFilterValue: value }));
  }, []);

  return {
    sorting: tableState.sorting,
    setSorting,
    columnFilters: tableState.columnFilters,
    setColumnFilters,
    globalFilter: tableState.globalFilter,
    setGlobalFilter,
    globalFilterValue: tableState.globalFilterValue,
    setGlobalFilterValue,
  };
};
